import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, FileText, IndianRupee, Undo2, Pencil, Archive, ArchiveRestore, Phone, Mail, MapPin, BadgeCheck, Loader2, AlertCircle, CheckCircle2, X, CalendarClock,
} from 'lucide-react';
import GlobalDateFilter from '../../components/GlobalDateFilter/GlobalDateFilter';
import styles from './Vendors.module.css';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { vendorFetch, errText, inr, inr2, fmtDate, balanceText, modeLabel, ymd } from '../../utils/vendorApi';
import VendorForm from './components/VendorForm';
import PurchaseBillModal from './components/PurchaseBillModal';
import PaymentModal from './components/PaymentModal';
import ReturnModal from './components/ReturnModal';
import { BillsTable, PaymentsTable, StatusPill } from './components/VendorTables';

const KIND = {
    OPENING: { label: 'Opening', tone: 'grey' }, BILL: { label: 'Bill', tone: 'purple' }, PAYMENT: { label: 'Payment', tone: 'green' },
    RETURN: { label: 'Return', tone: 'amber' }, REFUND: { label: 'Refund in', tone: 'grey' },
};

export default function VendorDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const { user, hasPermission } = useAuth();
    const storeId = activeStore?._id || activeStore?.id;
    const owner = user?.role === 'SUPERADMIN';
    const can = (a) => owner || hasPermission('vendors', a);

    const [data, setData] = useState(null);
    const [tab, setTab] = useState('khata');
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [msg, setMsg] = useState(null);
    const ALL_TIME = { startDate: '2000-01-01', endDate: ymd(), label: 'All time' };
    const [range, setRange] = useState(ALL_TIME);
    const isAll = range.startDate === ALL_TIME.startDate;
    const inRange = (d) => { if (isAll || !d) return true; const k = ymd(d); return k >= range.startDate && k <= range.endDate; };
    const flash = (type, text) => { setMsg({ type, text }); if (type === 'ok') setTimeout(() => setMsg(null), 4000); };

    const load = useCallback(async () => {
        setLoading(true);
        try { setData((await vendorFetch(`/${id}`, { storeId })).data); } catch (e) { flash('error', errText(e)); } finally { setLoading(false); }
    }, [id, storeId]);
    useEffect(() => { load(); }, [load]);

    const done = (d, message) => { setModal(null); flash('ok', message || 'Saved.'); load(); };
    const open = (type, props = {}) => setModal({ type, props });

    const toggleArchive = async () => {
        const v = data.vendor;
        if (v.isActive && !window.confirm(`Archive ${v.name}? It will be hidden from new bills. Its history stays.`)) return;
        try { const j = await vendorFetch(`/${v._id}`, { method: 'PUT', storeId, body: { isActive: !v.isActive } }); flash('ok', j.message); load(); } catch (e) { flash('error', errText(e)); }
    };
    const cancelBill = async (b) => {
        const reason = window.prompt(`Cancel bill ${b.billNo || b.refNo}? Its stock will be removed.\n\nWhy?`);
        if (!reason) return;
        try { const j = await vendorFetch(`/bills/${b._id}/cancel`, { method: 'PATCH', storeId, body: { reason } }); flash('ok', j.message); load(); } catch (e) { flash('error', errText(e)); }
    };
    const voidPayment = async (p) => {
        const reason = window.prompt(`Void the ${inr(p.amount)} payment? The bills it paid will be open again.\n\nWhy?`);
        if (!reason) return;
        try { const j = await vendorFetch(`/payments/${p._id}/void`, { method: 'PATCH', storeId, body: { reason } }); flash('ok', j.message); load(); } catch (e) { flash('error', errText(e)); }
    };

    if (loading && !data) return <div className={styles.page}><div className={styles.empty}><Loader2 size={20} className={styles.spin} /></div></div>;
    if (!data) return <div className={styles.page}><div className={styles.empty}><strong>Vendor not found</strong>{msg?.text}<div style={{ marginTop: 12 }}><button type="button" className={styles.btn} onClick={() => navigate('/inventory/vendors')}><ArrowLeft size={14} /> All vendors</button></div></div></div>;

    const { vendor: v, summary: s = {} } = data;
    // the date range picks which lines are shown; balances stay worked out from the full history
    const bills = data.bills.filter((b) => inRange(b.billDate));
    const payments = data.payments.filter((p) => inRange(p.date));
    const returns = data.returns.filter((r) => inRange(r.date));
    const ledger = data.ledger.filter((e) => inRange(e.date));
    const bt = balanceText(s.balance || 0);
    const openBills = bills.filter((b) => b.balance > 0.01 && b.status !== 'CANCELLED');

    return (
        <div className={styles.page}>
            <div className={styles.profile}>
                <button type="button" className={styles.iconBtn} onClick={() => navigate('/inventory/vendors')} aria-label="All vendors"><ArrowLeft size={15} /></button>
                <span className={styles.avatar}>{v.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}</span>
                <div className={styles.profileText}>
                    <h2>{v.name} <span className={`${styles.pill} ${styles[`pill_${bt.tone}`]}`}>{bt.text}</span>{!v.isActive && <span className={`${styles.pill} ${styles.pill_grey}`}>Archived</span>}</h2>
                    <p>
                        {v.contactPerson && <span>{v.contactPerson}</span>}
                        {v.phone && <span><Phone size={11} /> <a href={`tel:${v.phone}`} style={{ color: 'inherit' }}>{v.phone}</a></span>}
                        {v.email && <span><Mail size={11} /> {v.email}</span>}
                        {v.gstin && <span><BadgeCheck size={11} /> {v.gstin}</span>}
                        <span><CalendarClock size={11} /> {v.paymentTermsDays ? `${v.paymentTermsDays} days credit` : 'Pay on delivery'}</span>
                        {v.address && <span><MapPin size={11} /> {v.address}</span>}
                    </p>
                </div>
                <div className={styles.actions}>
                    {can('manage') && <button type="button" className={styles.btn} onClick={() => open('vendor', { vendor: v })}><Pencil size={13} /> Edit</button>}
                    {can('manage') && <button type="button" className={styles.btn} onClick={toggleArchive}>{v.isActive ? <><Archive size={13} /> Archive</> : <><ArchiveRestore size={13} /> Restore</>}</button>}
                    {can('record_return') && <button type="button" className={styles.btn} onClick={() => open('return', { vendorId: v._id })}><Undo2 size={13} /> Return</button>}
                    {can('record_payment') && <button type="button" className={styles.btn} onClick={() => open('pay', { vendorId: v._id })}><IndianRupee size={13} /> Pay</button>}
                    {can('record_purchase') && v.isActive && <button type="button" className={styles.btnPrimary} onClick={() => open('bill', { vendorId: v._id })}><FileText size={13} /> New bill</button>}
                </div>
            </div>

            {msg && <div className={`${styles.msg} ${msg.type === 'ok' ? styles.msgOk : styles.msgErr}`} role="status">{msg.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}<span>{msg.text}</span><button type="button" onClick={() => setMsg(null)} aria-label="Close"><X size={13} /></button></div>}

            <section className={styles.kpis}>
                <div className={`${styles.kpi} ${styles.kpiDark}`}><small>{s.balance < -0.009 ? 'Vendor owes you' : 'You owe'}</small><strong>{inr(Math.abs(s.balance || 0))}</strong><em>{openBills.length} bills open{s.opening ? ` · opening ${inr(s.opening)}` : ''}</em></div>
                <div className={styles.kpi}><small>Bought</small><strong>{inr(s.purchased)}</strong><em>{s.bills || 0} bills{s.gst > 0 ? ` · GST ${inr(s.gst)}` : ''}</em></div>
                <div className={styles.kpi}><small>Paid</small><strong>{inr(s.paid)}</strong><em>{s.lastPaid ? `Last ${fmtDate(s.lastPaid)}` : 'Nothing paid yet'}</em></div>
                <div className={styles.kpi}><small>Returned</small><strong>{inr(s.returned)}</strong><em>{s.returns || 0} returns{s.refunds ? ` · refunded ${inr(s.refunds)}` : ''}</em></div>
                <div className={`${styles.kpi} ${s.overdue > 0 ? styles.kpiRed : ''}`}><small>Overdue</small><strong>{inr(s.overdue)}</strong><em>Due in 7 days {inr(s.dueSoon)}</em></div>
                {(s.pending > 0 || s.advance > 0) && <div className={`${styles.kpi} ${s.pending > 0 ? styles.kpiAmber : ''}`}><small>Not cleared / advance</small><strong>{inr(s.pending)}</strong><em>Advance with vendor {inr(s.advance)}</em></div>}
            </section>

            <div className={styles.toolbar}>
                <GlobalDateFilter startDate={isAll ? '' : range.startDate} endDate={isAll ? '' : range.endDate} onChange={(r) => r?.startDate && setRange(r)} defaultLabel={isAll ? 'All time' : (range.label || 'Date range')} />
                {!isAll && <button type="button" className={`${styles.btnGhost} ${styles.small}`} onClick={() => setRange(ALL_TIME)}>Show all</button>}
                <div className={styles.tabs} role="tablist">
                    {[['khata', 'Khata', ledger.length], ['bills', 'Bills', bills.length], ['payments', 'Payments', payments.length], ['returns', 'Returns', returns.length]].map(([k, label, n]) => (
                        <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? styles.tabOn : ''} onClick={() => setTab(k)}>{label} <b>{n}</b></button>
                    ))}
                </div>
            </div>

            <section className={styles.card}>
                {tab === 'khata' && (ledger.length === 0 ? <div className={styles.empty}>{isAll ? 'No entries yet.' : 'No entries in this period.'}</div> : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>Date</th><th>Entry</th><th className={styles.right}>Bill / refund (+)</th><th className={styles.right}>Paid / returned (−)</th><th className={styles.right}>Balance</th></tr></thead>
                            <tbody>
                                {ledger.map((e, i) => {
                                    const k = KIND[e.kind] || KIND.BILL;
                                    const b = balanceText(e.balance);
                                    return (
                                        <tr key={`${e.kind}-${e.id || i}-${i}`}>
                                            <td className={styles.num}>{fmtDate(e.date)}</td>
                                            <td>
                                                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <span className={`${styles.pill} ${styles[`pill_${k.tone}`]}`}>{k.label}</span>{e.label}
                                                    {e.kind === 'BILL' && <StatusPill bill={{ status: e.status, overdue: e.overdue }} />}
                                                    {e.kind === 'PAYMENT' && e.clearStatus === 'PENDING' && <span className={`${styles.pill} ${styles.pill_amber}`}>Not cleared</span>}
                                                </span>
                                            </td>
                                            <td className={`${styles.right} ${styles.num}`}>{e.debit ? inr2(e.debit) : ''}</td>
                                            <td className={`${styles.right} ${styles.num} ${styles.green}`}>{e.credit ? inr2(e.credit) : ''}</td>
                                            <td className={`${styles.right} ${styles.num} ${styles[b.tone]}`} style={{ fontWeight: 900 }}>{e.balance > 0.009 ? inr2(e.balance) : e.balance < -0.009 ? `${inr2(-e.balance)} cr` : '0'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ))}
                {tab === 'bills' && (
                    <BillsTable bills={bills} showVendor={false}
                        onPay={can('record_payment') ? (b) => open('pay', { vendorId: v._id, billId: b._id }) : null}
                        onReturn={can('record_return') ? (b) => open('return', { vendorId: v._id, billId: b._id }) : null}
                        onCancel={can('void') ? cancelBill : null} />
                )}
                {tab === 'payments' && (
                    <PaymentsTable payments={payments} storeId={storeId} showVendor={false} canClear={can('clear_payment')} canVoid={can('void')}
                        onChanged={(d, m, isErr) => { flash(isErr ? 'error' : 'ok', m); if (!isErr) load(); }} onVoid={voidPayment} />
                )}
                {tab === 'returns' && (returns.length === 0 ? <div className={styles.empty}>No stock returned to this vendor.</div> : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>Date</th><th>Items</th><th>Bill</th><th className={styles.right}>Value</th><th>Settled</th><th>Reason</th></tr></thead>
                            <tbody>
                                {returns.map((r) => (
                                    <tr key={r._id}>
                                        <td className={styles.num}>{fmtDate(r.date)}</td>
                                        <td><span className={styles.stack}>{r.items.map((it, i) => <small key={i}><b style={{ color: 'var(--h-color-neutral-900)' }}>{it.qty} ×</b> {it.name}</small>)}</span></td>
                                        <td>{r.billRefNo || '—'}</td>
                                        <td className={`${styles.right} ${styles.num}`} style={{ fontWeight: 900 }}>{inr2(r.total)}</td>
                                        <td>{r.settlement === 'REFUND' ? <span className={`${styles.pill} ${styles.pill_green}`}>Refund · {modeLabel(r.refundMode)}</span> : <span className={`${styles.pill} ${styles.pill_purple}`}>Taken off bill</span>}</td>
                                        <td className={styles.muted}>{r.reason || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </section>

            {v.notes && <section className={styles.card} style={{ padding: '12px 14px' }}><span className={styles.sectionLabel}>Notes</span><p style={{ margin: '6px 0 0', fontWeight: 600, whiteSpace: 'pre-wrap' }}>{v.notes}</p></section>}

            {modal?.type === 'vendor' && <VendorForm storeId={storeId} {...modal.props} onClose={() => setModal(null)} onSaved={done} />}
            {modal?.type === 'bill' && <PurchaseBillModal storeId={storeId} canPay={can('record_payment')} {...modal.props} onClose={() => setModal(null)} onSaved={done} />}
            {modal?.type === 'pay' && <PaymentModal storeId={storeId} {...modal.props} onClose={() => setModal(null)} onSaved={done} />}
            {modal?.type === 'return' && <ReturnModal storeId={storeId} {...modal.props} onClose={() => setModal(null)} onSaved={done} />}
        </div>
    );
}
